import axios from "axios";

// Governed runtime authority for the ARI studio:
//   ari_source SQL -> Common API -> E1 backend same-origin proxy -> this client.
// The browser NEVER sees the API token; the E1 backend proxy injects it server-side.
// FAIL CLOSED: every call throws on non-2xx / network error. There is NO static
// financial fallback and NO frontend semantic reconstruction of governed data.
const ARI_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/annual-report-intelligence/v1`;

export const ARI_ACTIVE_SOURCE = "GOVERNED_COMMON_API";

// Governed fact/value status vocabulary (spec-level constants; preserved verbatim).
export const ARI_STATUS = [
  "SOURCE_CANDIDATE", "EXTRACTED_RAW", "RESOLVED_PROVISIONAL", "MAPPED_PROVISIONAL",
  "CERTIFIED", "REVIEW_REQUIRED", "PAGE_PROVEN_ATOM_PENDING",
];
export const ARI_VALUE_AUTHORITY = [
  "AUDITED_ACTUAL", "REPORTED_ACTUAL", "MANAGEMENT_ESTIMATE",
  "MANAGEMENT_GUIDANCE", "AI_DERIVED_INFERENCE",
];
export const ARI_STATUS_DESC = {
  SOURCE_CANDIDATE: "Candidate occurrence detected in source; not yet resolved to a unique fact.",
  EXTRACTED_RAW: "Value captured exactly as printed; no normalisation applied.",
  RESOLVED_PROVISIONAL: "Provisionally resolved to a single entity; pending confirmation.",
  MAPPED_PROVISIONAL: "Provisionally mapped to the canonical field; pending confirmation.",
  CERTIFIED: "Verified and certified against governed evidence.",
  REVIEW_REQUIRED: "Flagged for mandatory human review.",
  PAGE_PROVEN_ATOM_PENDING: "Row proven to its source page; atom-level proof still pending.",
  NUMBER: "Numeric value captured from source.",
};
export const ARI_VALUE_AUTHORITY_DESC = {
  AUDITED_ACTUAL: "Audited historical actual.",
  REPORTED_ACTUAL: "Reported (unaudited) actual.",
  MANAGEMENT_ESTIMATE: "Management estimate.",
  MANAGEMENT_GUIDANCE: "Forward-looking management guidance.",
  AI_DERIVED_INFERENCE: "AI-derived inference — never a reported management fact.",
};

// Business-journey → ari_source tables that feed each domain view (used by the
// v1.1 Certification-Studio overview surface for awaiting-source guidance only).
export const ARI_DOMAIN_TABLES = {
  governance: ["people_master", "company_person_roles", "board_committees", "committee_memberships"],
  audit: ["auditors", "report_auditors", "audit_opinions", "key_audit_matters"],
  shareholding: ["shareholding_snapshots", "shareholders", "shareholder_holdings"],
  guidance: ["estimates_and_guidance"],
  identity: ["company_master", "company_identifiers", "addresses", "company_addresses"],
  reports: ["annual_reports", "report_documents", "report_pages", "report_content_blocks", "report_content_atoms"],
  disclosures: ["report_sections", "section_taxonomy", "section_classifications", "fact_observations"],
  dictionary: ["field_dictionary", "field_observations", "schema_evolution_log"],
  quality: ["extraction_runs", "review_queue", "authum_row_source_manifest"],
};

// ---------------- Governed Common API client (fail-closed) ----------------
async function ariGet(path, params) {
  // axios throws on non-2xx and on network/timeout -> callers fail closed.
  const r = await axios.get(`${ARI_V1}${path}`, { params, timeout: 20000 });
  return r.data;
}

// Runtime bootstrap: governed meta + report corpus. Throws (fails closed) on any error.
export async function loadAriRuntime() {
  const [meta, reports] = await Promise.all([ariGet("/meta"), ariGet("/report")]);
  return { activeSource: ARI_ACTIVE_SOURCE, meta, reports: Array.isArray(reports) ? reports : [] };
}

export const loadReport = (reportId) => ariGet(`/report/${reportId}`);
export const loadReportStatements = (reportId) => ariGet(`/report/${reportId}/statements`);
export const loadStatement = (statementId, basis) =>
  ariGet(`/statement/${statementId}`, basis ? { basis } : undefined);
export const loadValueProvenance = (valueId) => ariGet(`/value/${valueId}/provenance`);
export const loadNote = (noteId) => ariGet(`/note/${noteId}`);
export const loadDocumentPage = (reportId, documentId, pdfPageNumber) =>
  ariGet(`/report/${reportId}/document/${documentId}/page/${pdfPageNumber}`);

// GAP-A: governed company identity is delivered on the /report payload
// (company_display_name / company_legal_name). The client reads it there directly;
// no separate route, no static map, no fabrication.

// ---------------- Governed grouping (by governed IDs, not geometry) ----------------
// The Common API returns /statement/{id} as a denormalised row×column×value join.
// We regroup STRICTLY by governed row_id / column_id — no x-position, no /total/i,
// no numeric regex, no sentinel collapsing.
export function groupStatement(payload) {
  const flat = Array.isArray(payload?.rows) ? payload.rows : [];
  const columns = [];
  const colSeen = new Set();
  const rows = [];
  const rowSeen = new Set();
  const valueByCell = new Map(); // `${row_id}::${column_id}` -> governed value object

  flat.forEach((f) => {
    if (f.column_id != null && !colSeen.has(f.column_id)) {
      colSeen.add(f.column_id);
      columns.push({
        column_id: f.column_id,
        column_order: f.column_order,
        heading: f.column_heading_as_disclosed,
        period_role: f.period_role,
        period_end: f.period_end,
        unit: f.column_unit,
      });
    }
    if (f.row_id != null && !rowSeen.has(f.row_id)) {
      rowSeen.add(f.row_id);
      rows.push({
        row_id: f.row_id,
        parent_row_id: f.parent_row_id,
        row_order: f.row_order,
        row_type: f.row_type,
        label: f.source_label_exact,
        source_row_text: f.source_row_text,
        note_reference: f.note_reference,
        indentation_level: f.indentation_level,
        section_path: f.section_path,
        mapping_status: f.mapping_status,
        source_page_id: f.source_page_id,
      });
    }
    if (f.value_id != null && f.row_id != null && f.column_id != null) {
      valueByCell.set(`${f.row_id}::${f.column_id}`, {
        value_id: f.value_id,
        raw_value_exact: f.raw_value_exact,
        numeric_value: f.numeric_value,
        value_semantics: f.value_semantics,
        currency: f.value_currency,
        scale: f.scale,
        review_status: f.value_review_status,
      });
    }
  });

  columns.sort((a, b) => (a.column_order ?? 0) - (b.column_order ?? 0));
  rows.sort((a, b) => (a.row_order ?? 0) - (b.row_order ?? 0));

  return {
    statement: {
      statement_id: payload.statement_id,
      report_id: payload.report_id,
      statement_type: payload.statement_type,
      statement_role: payload.statement_role,
      title: payload.statement_title_as_disclosed,
      page_start: payload.page_start,
      page_end: payload.page_end,
      unit: payload.unit_as_disclosed,
    },
    columns,
    rows,
    valueByCell,
    counts: { rows: rows.length, values: valueByCell.size },
  };
}

// Governed value-state classification for rendering (§6). NUMBER/actual states render
// the value; explicit "not stated / ambiguous / insufficient" states render a token.
const RENDERABLE_SEMANTICS = new Set(["NUMBER", "NUMERIC", "EXPLICITLY_CAPTURED", "REPORTED_ACTUAL", "AUDITED_ACTUAL"]);
export function valueDisplay(v) {
  if (!v) return { render: false, token: "No value", governed: null, semantics: "NO_VALUE" };
  const sem = String(v.value_semantics || "").toUpperCase();
  const governed = v.raw_value_exact != null ? v.raw_value_exact : v.numeric_value;
  if (RENDERABLE_SEMANTICS.has(sem) && governed != null && String(governed).trim() !== "") {
    return { render: true, governed: String(governed), semantics: sem, review: v.review_status };
  }
  const tokens = {
    NOT_STATED_IN_SOURCE: "Not stated",
    AMBIGUOUS_IN_SOURCE: "Ambiguous",
    SOURCE_EXTRACTION_INSUFFICIENT: "Extraction insufficient",
    SOURCE_DASH: "–",
    NOT_CAPTURED: "Not captured",
    NOT_RUN: "Not run",
  };
  return { render: false, token: tokens[sem] || (sem || "No value"), governed: null, semantics: sem, review: v.review_status };
}
